import React from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, RiskFactorScores, FactorSeverity } from '../RiskAreasWidget';
import { ChevronRight, MapPin, Users, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, FreshnessBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantBList({
  areas,
  onSelect
}: Props) {
  if (areas.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center py-16" data-id="element-2855">
        <CheckCircle2 size={48} className="text-emerald-400 mb-4" data-id="element-2856" />
        <p className="text-lg font-medium text-gray-900" data-id="element-2857">No activities found</p>
      </div>;
  return <div className="divide-y divide-gray-100" data-id="element-2858">
      {areas.map((area, index) => {
      const config = RISK_LEVEL_CONFIG[area.riskLevel];
      const statusConfig = STATUS_CONFIG[area.status];
      const StatusIcon = statusConfig.icon;
      const band = getRiskBand(area.riskScore);
      const activeFactors = (Object.entries(area.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, severity]) => severity !== 'none').sort((a, b) => {
        const order = {
          high: 0,
          medium: 1,
          low: 2,
          none: 3
        };
        return order[a[1]] - order[b[1]];
      }).slice(0, 3);
      const dataGap = hasDataGap(area);
      let rowBg = 'hover:bg-gray-50';
      if (area.sifPotential) {
        rowBg = 'bg-red-50/60 hover:bg-red-50 border-l-4 border-red-700';
      } else if (dataGap) {
        rowBg = 'bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-dashed border-amber-400';
      } else if (area.status === 'closed') {
        rowBg = 'bg-emerald-50/30 opacity-75 hover:bg-emerald-50/50 border-l-4 border-transparent';
      } else if (area.status === 'measures_taken') {
        rowBg = 'bg-amber-50/30 hover:bg-amber-50/50 border-l-4 border-transparent';
      } else {
        rowBg += ' border-l-4 border-transparent';
      }
      return <motion.button key={area.id} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: index * 0.02
      }} onClick={() => onSelect(area)} className={`w-full text-left px-6 py-3.5 flex items-center gap-4 transition-colors group ${rowBg}`} data-id="element-2859">
            {/* Risk score band */}
            <div className={`flex-shrink-0 w-14 py-1.5 rounded-md ${config.bgColor} flex flex-col items-center`} data-id="element-2860">
              <span className={`text-xs font-bold ${config.color}`} data-id="element-2861">
                {band.short}
              </span>
              <span className={`text-[9px] ${config.color} opacity-70`} data-id="element-2862">
                {area.riskScore}
              </span>
            </div>

            {/* Location info */}
            <div className="flex-1 min-w-0" data-id="element-2863">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-2864">
                {area.sifPotential && <div className="mr-1" data-id="element-2865">
                    <SifBadge data-id="element-2866" />
                  </div>}
                <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-2867">
                  {area.project}
                </span>
                <ChevronRight size={12} className="text-gray-300 flex-shrink-0" data-id="element-2868" />
                <span className="text-sm text-gray-600 truncate" data-id="element-2869">
                  {area.floor}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2870">
                <p className="text-xs text-gray-400 truncate" data-id="element-2871">
                  {area.location}
                </p>
                <ScheduledDateBadge date={area.scheduledDate} data-id="element-2872" />
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2 flex-shrink-0 w-28" data-id="element-2873">
              <span className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-2874">
                <StatusIcon size={10} data-id="element-2875" />
                {statusConfig.label}
              </span>
            </div>

            {/* Factors */}
            <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0 w-48" data-id="element-2876">
              {activeFactors.map(([factor, severity]) => {
            const factorConf = FACTOR_CONFIG[factor];
            const sevColors = FACTOR_SEVERITY_COLORS[severity];
            return <div key={factor} title={factorConf.description} className={`flex items-center gap-1 px-2 py-0.5 rounded-full cursor-help ${sevColors.bg}`} data-id="element-2877">
                    <span className={`w-1.5 h-1.5 rounded-full ${sevColors.dot}`} data-id="element-2878" />
                    <span className={`text-[10px] font-medium ${sevColors.text}`} data-id="element-2879">
                      {factorConf.label}
                    </span>
                  </div>;
          })}
            </div>

            {/* Freshness & Data Quality */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0 justify-end" data-id="element-2880">
              <DataConfidenceBadge confidence={area.dataConfidence} data-id="element-2881" />
              <FreshnessBadge lastUpdated={area.lastUpdated} data-id="element-2882" />
            </div>

            <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0 ml-2" data-id="element-2883" />
          </motion.button>;
    })}
    </div>;
}